import { html, css, TemplateResult, nothing } from 'lit';
import { customElement, property, query, state } from 'lit/decorators.js';
import debounce from 'lodash/debounce';
import { BaseComponent } from '../base-component/base-component';

@customElement('mhecomm-search-input')
export class SearchComponent extends BaseComponent {
  static styles = css`
    :host {
      display: block;
      width: 100%;
      padding: 0px !important;
    }

    .uxt-mhcorp {
      height: 100%;
    }

    input {
      height: 100%;
      width: 100%;
      border: none;
      outline: none;
      font-size: 1rem;
      padding: 0.375rem 0.75rem;
    }

    .dropdown-item:hover, .dropdown-item:focus, .dropdown-item.bg-light {
      font-weight: bold;
    }
  `;
  @query('.dropdown-menu') dropdownEl!: HTMLDivElement;
  @query('input') inputEl!: HTMLInputElement;

  /**
   * The static list of items to search from.
   * Can be an array of strings or objects.
   * Ignored if fetchFn is provided.
   */
  @property({ type: Array })
  items: any[] = [];

  /**
   * Async function to fetch matching items based on the query string.
   * If provided, takes precedence over `items`.
   * Should return a Promise resolving to an array of items.
   */
  @property({ attribute: false })
  fetchFn: ((query: string | any) => Promise<any[]>) | null = null;

  /**
   * Function to render a single item in the dropdown.
   * Receives the item, a boolean indicating if it is highlighted, the index, and a handleSelect function.
   * Should return a lit-html TemplateResult.
   */
  @property({ attribute: false })
  renderItem:
    | ((item: string | any, highlighted: boolean, index: number, handleSelect: (item: any) => void) => TemplateResult)
    | null = null;

  /**
   * Callback fired when an item is selected.
   * Receives the selected item as argument.
   */
  @property({ attribute: false })
  onSelect: ((item: any) => void) | null = null;

  /**
   * Function to get the display label from an item.
   * Required if items are objects.
   * Receives an item and returns a string label.
   */
  @property({ attribute: false })
  getLabel: ((item: any) => string) | null = null;

  /**
   * Function to pass the input value in parent component
   * To fix Edge case issue fetchFn too updates the value but if not passed use this one
   */
  @property({ attribute: false })
  updateSearchInput: (value: string) => void = () => {};

  /** Placeholder text for the input */
  @property({ type: String })
  placeholder = 'Search...';

  /** Message to show when no results are found */
  @property({ type: String })
  noResultsMessage = 'No results found.';

  /** Currently selected value */
  @property({ type: Object })
  value: any = null;

  /** Minimum characters to trigger search when using fetchFn */
  @property({ type: Number })
  minSearchChars = 0;

  /** Optional classes to pass in to add to the input field */
  @property({ type: String })
  inputElClassList = '';

  @property({ type: Boolean })
  selectFirstOptionByDefault = false;

  @property({ type: Boolean })
  hideSuggestion = false;

  @property({ type: Boolean })
  updatePageTitle = false;

  @property({ type: String })
  pageTitleTemplate = 'Search results for "%query%"';

  @property({ type: String })
  defaultPageTitle = '';

  /** Loading state when fetchFn is in progress */
  @state() loading = false;

  /** Current array of search results */
  @state() results: any[] = [];

  /** Current input query string */
  @state() query = '';

  /** Whether the dropdown with search results is open */
  @state() dropdownOpen = false;

  /** Index of the currently highlighted item in dropdown */
  @state() highlightIndex = -1;

  /** Tracks whether Enter submit is temporarily blocked while input work is pending. */
  @state() isSubmitBlocked = false;

  /**
   * When true, Enter submit waits for pending debounce/fetch to settle.
   * Search page/support keep this enabled to avoid duplicate in-page dispatches.
   */
  @property({ type: Boolean })
  blockEnterOnPendingInput = false;

  /** Debounced input handler to limit search requests */
  private _handleInputDebounced = debounce(this._handleInput.bind(this), 250);

  connectedCallback(): void {
    super.connectedCallback();
    this.addTrackedEventListener(document, 'click', this._handleClickOutside.bind(this), true);
  }

  updated(changed: Map<string, unknown>) {
    if (changed.has('items')) {
      const first = this.items?.[0];
      if (typeof first === 'object' && this.getLabel == null) {
        throw new Error('[SearchInputComponent] getLabel() is required when using object items.');
      }
    }
  }

  renderComp() {
    const shouldShowNoResults = !this.loading && this.results.length === 0 && this.query.length >= this.minSearchChars;

    return html`
      <input
        id="search-input"
        type="text"
        .value=${this._inputDisplayValue}
        @input=${this.inputEvent.bind(this)}
        @keydown=${this._handleKeydown.bind(this)}
        @focus=${this._handleFocus}
        class="${this.inputElClassList}"
        placeholder=${this.placeholder}
        autocomplete="off"
        role="combobox"
        aria-autocomplete="both"
        aria-expanded=${this.dropdownOpen}
        aria-controls="listbox"
        aria-activedescendant=${this.dropdownOpen && this.highlightIndex >= 0 ? `item-${this.highlightIndex}` : nothing}
        aria-label=${this.placeholder}
        aria-describedby="search-input-guidance" />
      ${this._inputDisplayValue?.length < this.minSearchChars
        ? html`<div id="search-input-guidance" class="visually-hidden">
            Type at least 3 characters to see suggestions.
          </div>`
        : nothing}
      <div aria-live="polite" aria-atomic="true" class="visually-hidden">
        ${this.results?.length
          ? `${this.results.length} ${this.results.length === 1 ? 'suggestion' : 'suggestions'} available.`
          : shouldShowNoResults
          ? this.noResultsMessage
          : ''}
      </div>
      ${this.dropdownOpen && !this.hideSuggestion
        ? html`
            <div class="dropdown-menu show w-100" role="listbox" id="listbox" aria-label="Search results">
              ${this.loading
                ? html`<div class="dropdown-item disabled" role="option" aria-disabled="true" aria-live="polite">
                    Loading...
                  </div>`
                : shouldShowNoResults
                ? html`<div class="dropdown-item disabled" role="option" aria-disabled="true" aria-live="polite">
                    ${this.noResultsMessage}
                  </div>`
                : this.results.map(
                    (item, i) => html`
                      ${this.renderItem
                        ? this.renderItem(item, i === this.highlightIndex, i, this._handleSelect.bind(this))
                        : html`<button
                            class="dropdown-item ${i === this.highlightIndex ? 'bg-light' : ''}"
                            id="item-${i}"
                            role="option"
                            aria-selected=${i === this.highlightIndex}
                            @click=${() => this._handleSelect(item)}
                            tabindex="-1">
                            ${this._getItemLabel(item)}
                          </button>`}
                    `
                  )}
            </div>
          `
        : nothing}
    `;
  }

  private inputEvent(e: InputEvent) {
    this.isSubmitBlocked = true;
    this._handleInputDebounced((e.target as HTMLInputElement).value);
  }

  private get _inputDisplayValue(): string {
    if (!this.value) return this.query;
    return this._getItemLabel(this.value);
  }

  private _getItemLabel(item: any): string {
    if (typeof item === 'string') return item;
    if (!this.getLabel) throw new Error('[SearchInputComponent] getLabel() must be provided for object items.');
    return this.getLabel(item);
  }

  async _handleInput(value: string): Promise<void> {
    this.query = value;
    this.dropdownOpen = true;

    if (this.fetchFn && value.length < this.minSearchChars) {
      // Keep parent state in sync even when fetch is intentionally skipped.
      this.updateSearchInput(value);
      this._clearResults();
      this.isSubmitBlocked = false;
      return;
    }

    try {
      if (this.fetchFn) {
        this.loading = true;
        this.results = await this.fetchFn(value);
      } else {
        this.results = this.items.filter((item) =>
          this._getItemLabel(item).toLowerCase().includes(value.toLowerCase())
        );
      }
    } catch {
      this.results = [];
    } finally {
      this.isSubmitBlocked = false;
      this.loading = false;
      this.highlightIndex = this.selectFirstOptionByDefault && this.results.length ? 0 : -1;
    }
  }

  private _clearResults(): void {
    this.results = [];
    this.highlightIndex = -1;
    this.loading = false;
    this.dropdownOpen = false;
  }

  _handleKeydown(e: KeyboardEvent): void {
    // Edge case: handle submit action immediately after entering the ISBN in the input
    if (this.hideSuggestion) {
      this.updateSearchInput(this.inputEl.value);
      this.isSubmitBlocked = false;
    }

    if (e.key === 'ArrowDown') {
      e.preventDefault();

      if (!this.dropdownOpen && this.results.length > 0) {
        this.dropdownOpen = true;
        return;
      }

      if (this.dropdownOpen) {
        this.highlightIndex = (this.highlightIndex + 1) % this.results.length;
      }
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();

      if (this.dropdownOpen) {
        this.highlightIndex = (this.highlightIndex - 1 + this.results.length) % this.results.length;
      }
    } else if (e.key === 'Enter' || e.key === 'Tab') {
      if (this.dropdownOpen && this.highlightIndex >= 0) {
        // this code is to manaully trigger the click event on dropdown options
        const el = this.renderRoot.querySelector(`#item-${this.highlightIndex}`) as HTMLElement | null;
        if (e.key === 'Enter' && el && this.renderItem) {
          el.click();
        }

        this._handleSelect(this.results[this.highlightIndex]);
        this.dropdownOpen = false;
      }

      // submit the form if the input is inside a form
      if (e.key === 'Enter' && (!this.blockEnterOnPendingInput || !this.isSubmitBlocked)) {
        // Cancel pending debounce to prevent a stale suggestion fetch from
        // firing after submit and prematurely resetting isLoading.
        this._handleInputDebounced.cancel();

        // Sync the current input value to parent before submit so fast typing
        // does not result in a stale (debounce-lagged) search term.
        this.updateSearchInput(this.inputEl.value);

        if (this.updatePageTitle) {
          if (this.query) {
            this._updateDocumentTitle(this.query);
          } else {
            this._clearDocumentTitle();
          }
        }

        const form = this.closest('form');
        if (form) {
          form.requestSubmit(); // or form.submit()
        }
      }
    } else if (e.key === 'Escape') {
      this.dropdownOpen = false;
    }
  }

  _handleSelect(item: any): void {
    this.value = item;
    this.query = this._getItemLabel(item);
    this.dropdownOpen = false;

    if (this.updatePageTitle && this.query) {
      this._updateDocumentTitle(this.query);
    }

    this.onSelect?.(item);
  }

  _handleFocus(): void {
    if (this.fetchFn) {
      if (this.results.length > 0) {
        this.dropdownOpen = true;
      }
    } else {
      this.results = this.items.slice();
      this.dropdownOpen = true;
      this.highlightIndex = this.selectFirstOptionByDefault && this.results.length ? 0 : -1;
    }
  }

  _handleClickOutside(e: Event): void {
    const path = e.composedPath();
    const isClickInside = path.some((el) => el === this.dropdownEl || el === this.inputEl);

    if (isClickInside) {
      this.inputEl.focus();
    } else {
      this.dropdownOpen = false;
    }
  }

  public async refreshResults(): Promise<void> {
    if (this.query.trim().length === 0) {
      this._clearResults();
      return;
    }

    await this._handleInput(this.query);
  }

  _updateDocumentTitle(query: string): void {
    if (!query) return;
    document.title = this.pageTitleTemplate.replace('%query%', query);
  }

  _clearDocumentTitle(): void {
    if (this.defaultPageTitle) {
      document.title = this.defaultPageTitle;
    }
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'mhecomm-search-input': SearchComponent;
  }
}
